import React, { useEffect, useState, useRef, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
    Plus, Eye, Edit, Trash2, User, Wrench, Recycle,
    Check, Monitor, ImageIcon, Loader2, Upload, X,
    ArrowUpDown, Download, FileSpreadsheet, Settings2, MapPin
} from 'lucide-react';
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator
} from '../../components/ui/dropdown-menu';
import { assetService, Asset, AssetListFilters, AssetStatistics } from '../../services/assetService';
import { userService, User as UserType } from '../../services/userService';
import { departmentsService, Department } from '../../services/lookupService';
import { useToast } from '../../context/ToastContext';
import { authService } from '../../services/authService';
import { useCurrency } from '../../context/CurrencyContext';
import ConfirmModal from '../../components/Common/ConfirmModal';
import ActionDropdown, { ActionItem } from '../../components/Common/ActionDropdown';
import { PageHeader } from '../../components/shared/PageHeader';
import { masterService, Brand, Vendor, Lookup } from '../../services/masterService';
import { categoryService } from '../../services/categoryService';
import { exportToCsv } from '../../lib/exportCsv';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
    Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { Pagination } from '../../components/shared/Pagination';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { FormField } from '../../components/shared/FormField';
import { cn } from '../../lib/utils';
import UserSelect from '../../components/shared/UserSelect';
import { useForm } from '../../hooks/useForm';
import { deploymentTarget, deploymentTargetLabel } from './deploymentTarget';
import { getErrorMessage } from '../../lib/validationErrors';
import AssetImportPreview, { AssetImportPreviewRow, AssetImportPreviewSummary } from './AssetImportPreview';
import { useValidatedForm, CrossFieldValidator } from '../../hooks/useValidatedForm';
import {
    AssetAdvancedFilters,
    AdvancedFilterState,
    EMPTY_ADVANCED_FILTERS,
} from './AssetAdvancedFilters';
import { MultiSelectOption } from '../../components/shared/MultiSelect';
import { useManagementTableState } from '../../hooks/useManagementTableState';
import { FilterButton } from '../../components/shared/FilterButton';
import { ListTabBar, ListToolbar } from '../../components/shared/ListTabBar';
import { FilterChipsBar, FilterChip } from '../../components/shared/FilterChipsBar';
import { TableLoadingBar, TABLE_DIM_CLASS } from '../../components/shared/TableLoadingBar';

type TabKey = 'all' | 'available' | 'deployed' | 'maintenance' | 'disposed';

// Shared styling for the asset form's native <select> controls — mirrors the
// ui/Input sizing (44px touch target on mobile, compact on desktop).
const SELECT_CLS = "flex h-11 sm:h-9 w-full cursor-pointer rounded-md border border-input bg-background px-3 py-1 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50";

// Statuses that can be set by hand on the edit form. The lifecycle states
// (available / deployed / maintenance / disposed) belong to the Deploy,
// Undeploy, Maintenance and Dispose actions, which move the status and the
// assignment together — offering them here would let the two drift apart.
// AssetsService.update() enforces the same list server-side.
const MANUALLY_SETTABLE_STATUSES = ['lost', 'stolen', 'retired'];

// Dispose is blocked only while the asset is *actively deployed*. It used to be
// disabled whenever the asset carried a `location` or `site`, but those are
// descriptive fields written at creation and import — an available server
// labelled with its site is not deployed, and could never be written off.
// Disposal is terminal, so a disposed asset is excluded too.
// AssetsService.dispose() enforces the same rule server-side.
const canDispose = (status?: string) => status !== 'deployed' && status !== 'disposed';
const ASSET_STATUS_REASON_MAX_LENGTH = 500;

/**
 * Deploy-form rules that deliberately have no server counterpart.
 *
 * - deploymentDate: AssetsService.deploy() defaults an absent date to now, so
 *   the API accepts a deploy without one. The form has always required it; that
 *   is a UI choice and is kept here rather than tightened server-side, which
 *   would break API callers relying on the default.
 * - reason: the server requires it to be non-empty; the 5-character floor is a
 *   UI-only quality bar.
 *
 * Rules the DTO *can* express belong in the DTO, not here — this hatch is for
 * genuine client-only rules.
 */
const DEPLOY_FORM_RULES: CrossFieldValidator<Record<string, any>> = (values) => {
    const errors: Record<string, string> = {};

    if (!values.deploymentDate) {
        errors.deploymentDate = 'Deployment Date is required.';
    }
    if (values.reason && values.reason.trim().length < 5) {
        errors.reason = 'Reason for Deployment must be at least 5 characters.';
    }

    return errors;
};

/** Table/card-list empty state — a distinct message + affordance when a
 * search/advanced filter narrows the result set to zero, vs. the original
 * "nothing in the system yet" message when there's genuinely nothing there. */
const EmptyAssetsState: React.FC<{ hasFilters: boolean; onClearFilters: () => void }> = ({ hasFilters, onClearFilters }) => (
    <div className="flex flex-col items-center justify-center gap-3 py-10 text-center text-sm text-muted-foreground">
        <p>
            {hasFilters
                ? 'No assets match the selected filters.'
                : 'No assets found. Click "Add Asset" to create your first asset.'}
        </p>
        {hasFilters && (
            <Button type="button" variant="outline" size="sm" className="cursor-pointer" onClick={onClearFilters}>
                Clear Filters
            </Button>
        )}
    </div>
);

const AssetManagement: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [assets, setAssets] = useState<Asset[]>([]);
    const [totalAssets, setTotalAssets] = useState(0);
    const [tabCounts, setTabCounts] = useState<AssetStatistics | null>(null);
    const [users, setUsers] = useState<UserType[]>([]);
    const [departments, setDepartments] = useState<Department[]>([]);
    const [assetPhotos, setAssetPhotos] = useState<Record<number, number>>({});
    const [loading, setLoading] = useState(true);
    const [listLoading, setListLoading] = useState(false);
    const [showFilters, setShowFilters] = useState(false);
    const [currentUser, setCurrentUser] = useState<UserType | null>(null);
    const { showToast } = useToast();
    const { formatCost, availableCurrencies, defaultCurrency, symbolFor, convertBetween, formatInCurrency } = useCurrency();

    // Modal States
    const [showAssetModal, setShowAssetModal] = useState(false);
    const [showDeployModal, setShowDeployModal] = useState(false);
    const [showUndeployModal, setShowUndeployModal] = useState(false);
    const [showMaintenanceModal, setShowMaintenanceModal] = useState(false);
    const [showMaintenanceCompleteModal, setShowMaintenanceCompleteModal] = useState(false);
    const [showDisposalModal, setShowDisposalModal] = useState(false);
    const [showImportPreviewModal, setShowImportPreviewModal] = useState(false);
    const [importPreviewData, setImportPreviewData] = useState<AssetImportPreviewRow[]>([]);
    const [importPreviewSummary, setImportPreviewSummary] = useState<AssetImportPreviewSummary | undefined>(undefined);
    // Asset Tags from the uploaded file that the backend found already taken,
    // so the review screen can re-flag one the moment it is typed back in.
    const [takenAssetTagKeys, setTakenAssetTagKeys] = useState<string[]>([]);
    const [isImporting, setIsImporting] = useState(false);

    const [editMode, setEditMode] = useState(false);
    const [selectedAssetId, setSelectedAssetId] = useState<number | null>(null);

    // Master Data States
    interface Category { id: number; name: string; allowedTargetTypes?: string[]; isActive?: boolean; }
    const [masterCategories, setMasterCategories] = useState<Category[]>([]);
    const [masterBrands, setMasterBrands] = useState<Brand[]>([]);
    const [masterVendors, setMasterVendors] = useState<Vendor[]>([]);
    const [masterConditions, setMasterConditions] = useState<Lookup[]>([]);
    const [masterStatuses, setMasterStatuses] = useState<Lookup[]>([]);
    const [masterDisposalMethods, setMasterDisposalMethods] = useState<Lookup[]>([]);
    const [allowedTargetTypes, setAllowedTargetTypes] = useState<string[]>(['PERSON']);

    // Confirm Modal
    const [confirmState, setConfirmState] = useState<{
        show: boolean; title: string; message: string;
        onConfirm: () => void; type?: 'danger' | 'warning' | 'primary';
    }>({ show: false, title: '', message: '', onConfirm: () => { } });

    // Form Data
    const categories = ['laptop', 'desktop', 'mobile', 'tablet', 'printer', 'monitor', 'network', 'server', 'other'];
    const conditions = ['new', 'good', 'fair', 'poor', 'damaged'];

    const {
        values: assetFormData,
        errors: assetErrors,
        handleChange: handleAssetChange,
        handleBlur: handleAssetBlur,
        validateForm: validateAssetForm,
        resetForm: resetAssetForm,
        setValues: setAssetValues
    } = useForm({
        assetTag: '', name: '', category: '', condition: 'new', status: 'available', acquisitionType: 'purchased',
        brand: '', model: '', serialNumber: '', purchaseDate: '', purchaseCost: '', currency: 'INR', vendor: '',
        receivedFromVendorDate: '', vendorMonthlyRent: '', warrantyExpiry: '', usefulLifeYears: '5',
        salvageValue: '', location: '', notes: '',
        hostname: '', poNumber: '', invoiceNumber: '', costCenter: '', businessOwnerId: '',
        warrantyType: '', warrantyStart: '', maintenanceCycleDays: '',
        // The status the asset was loaded with. Never edited or submitted —
        // it only lets the reason rule below tell "changed" from "unchanged".
        originalStatus: 'available', statusChangeReason: '',
    }, {
        assetTag: { required: true, minLength: 3 },
        name: { required: true, minLength: 3 },
        category: { required: true },
        condition: { required: true },
        status: { required: true },
        purchaseCost: { min: 0 },
        usefulLifeYears: { min: 1 },
        statusChangeReason: {
            label: 'Reason',
            maxLength: ASSET_STATUS_REASON_MAX_LENGTH,
            // Conditionally required: only when the status is actually being
            // moved by hand. Mirrors the same rule in AssetsService.update().
            custom: (value, all) =>
                all.status !== all.originalStatus && !String(value ?? '').trim()
                    ? 'Please give a reason for the status change.'
                    : null,
        },
    });

    // Original-currency amount + its equivalent in the org default currency, shown
    // under cost inputs whenever the form's currency differs from the default.
    const formCurrencySymbol = symbolFor(assetFormData.currency || defaultCurrency);
    const renderDefaultCurrencyHint = (value: string) => {
        const amount = parseFloat(value);
        if (!amount || !assetFormData.currency || assetFormData.currency === defaultCurrency) return null;
        return (
            <p className="text-xs text-muted-foreground mt-1">
                ≈ {formatInCurrency(convertBetween(amount, assetFormData.currency, defaultCurrency), defaultCurrency)} {defaultCurrency}
            </p>
        );
    };

    const {
        values: deployFormData,
        errors: deployErrors,
        isRequired: isDeployFieldRequired,
        handleChange: handleDeployChange,
        handleBlur: handleDeployBlur,
        validateForm: validateDeployForm,
        applyServerErrors: applyDeployServerErrors,
        resetForm: resetDeployForm,
        setValues: setDeployValues
    } = useValidatedForm({
        formKey: 'asset.deploy',
        initialValues: {
            targetType: 'PERSON',
            userId: '',
            location: '',
            site: '',
            building: '',
            floor: '',
            roomDesk: '',
            deploymentDate: '',
            reason: ''
        } as Record<string, any>,
        labels: {
            targetType: 'Assignment Target',
            userId: 'Assign To User',
            location: 'Specific Location',
            deploymentDate: 'Deployment Date',
            reason: 'Reason for Deployment'
        },
        crossFieldValidators: [DEPLOY_FORM_RULES]
    });

    const {
        values: undeployFormData,
        errors: undeployErrors,
        handleChange: handleUndeployChange,
        handleBlur: handleUndeployBlur,
        validateForm: validateUndeployForm,
        resetForm: resetUndeployForm
    } = useForm({
        reason: '',
        condition: ''
    }, {
        reason: { required: true, minLength: 5 },
        condition: { required: true }
    });

    const {
        values: maintenanceFormData,
        errors: maintenanceErrors,
        handleChange: handleMaintenanceChange,
        handleBlur: handleMaintenanceBlur,
        validateForm: validateMaintenanceForm,
        resetForm: resetMaintenanceForm
    } = useForm({
        lastMaintenanceDate: '', nextMaintenanceDate: '', maintenanceNotes: '',
    }, {
        lastMaintenanceDate: { required: true },
        nextMaintenanceDate: { required: true },
    });

    const {
        values: disposalFormData,
        errors: disposalErrors,
        handleChange: handleDisposalChange,
        handleBlur: handleDisposalBlur,
        validateForm: validateDisposalForm,
        resetForm: resetDisposalForm
    } = useForm({
        disposalDate: '', disposalMethod: '', disposalReason: '', disposalNotes: '',
    }, {
        disposalDate: { required: true },
        disposalMethod: { required: true },
        disposalReason: { required: true, minLength: 5 }
    });

    const {
        values: maintenanceCompleteFormData,
        errors: maintenanceCompleteErrors,
        handleChange: handleMaintenanceCompleteChange,
        handleBlur: handleMaintenanceCompleteBlur,
        validateForm: validateMaintenanceCompleteForm,
        resetForm: resetMaintenanceCompleteForm
    } = useForm({
        maintenanceCompletedDate: '', workPerformed: '', completionNotes: '', nextMaintenanceDate: '',
    }, {
        maintenanceCompletedDate: { required: true },
        workPerformed: { required: true, minLength: 5 }
    });

    const [submittingAsset, setSubmittingAsset] = useState(false);
    const [submittingAction, setSubmittingAction] = useState(false);

    // Image upload state
    const [selectedImages, setSelectedImages] = useState<File[]>([]);
    const [imagePreviewUrls, setImagePreviewUrls] = useState<string[]>([]);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const importInputRef = useRef<HTMLInputElement>(null);

    /** Fetches the current page for a settled query state — the single
     * fetch trigger the shared hook calls after every user action (tab
     * switch, filter apply, sort, page/size change, or debounced search).
     * The AbortSignal cancels a still-in-flight request from a state that's
     * already been superseded, so a slow response can never clobber a
     * fresher one — and the table's existing rows stay visible the whole
     * time (only `listLoading` toggles a dim overlay). */
    const loadAssetsForState = async (
        qState: { tab: string; search: string; filters: AdvancedFilterState; sortBy?: string; sortDir?: 'asc' | 'desc'; page: number; pageSize: number },
        signal: AbortSignal,
    ) => {
        try {
            setListLoading(true);
            const result = await assetService.getAssets({
                ...qState.filters,
                tab: qState.tab as AssetListFilters['tab'],
                search: qState.search || undefined,
                sortBy: qState.sortBy,
                sortDir: qState.sortDir,
                page: qState.page,
                limit: qState.pageSize,
            }, signal);
            setAssets(result.data);
            setTotalAssets(result.total);

            const photoMap: Record<number, number> = {};
            result.data.forEach(asset => {
                photoMap[asset.id] = asset.photoCount ?? asset.photos?.length ?? 0;
            });
            setAssetPhotos(photoMap);
        } catch (error: any) {
            if (error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError') return;
            console.error('Failed to load assets', error);
            const msg = error?.response?.data?.message || error?.message || 'Unknown error';
            showToast(`Failed to load assets: ${msg}`, 'error');
        } finally {
            setListLoading(false);
        }
    };

    const qts = useManagementTableState<AdvancedFilterState>({
        emptyFilters: EMPTY_ADVANCED_FILTERS,
        defaultTab: 'all',
        onStateChange: loadAssetsForState,
    });

    /** Builds the AssetListFilters payload for one-off calls that fall
     * outside the hook's own fetch cycle (CSV export, duplicate pre-checks). */
    const buildListFilters = (forExport = false): AssetListFilters => ({
        ...qts.state.filters,
        tab: qts.state.tab as AssetListFilters['tab'],
        search: qts.state.search || undefined,
        sortBy: qts.state.sortBy,
        sortDir: qts.state.sortDir,
        ...(forExport ? {} : { page: qts.state.page, limit: qts.state.pageSize }),
    });

    /** Status-tab badge counts — permission-scoped like the list, but
     * intentionally NOT re-filtered by search/advanced filters (matches the
     * page's long-standing behavior: tabs always show totals for the tab
     * itself, not "results within the current filter"). */
    const loadTabCounts = async () => {
        try {
            setTabCounts(await assetService.getStatistics());
        } catch (e) { console.error('Tab counts fail', e); }
    };

    /** Users, master data, and departments — loaded once; used by both the
     * asset form and the advanced-filters dropdowns. */
    const loadStaticData = async () => {
        try {
            const usersData = await userService.getUsers();
            setUsers(usersData.filter((u: UserType) => u.isActive));
        } catch (e) { console.error('Users fail', e); }

        categoryService.getCategories().then(setMasterCategories).catch(e => console.error('Cats fail', e));
        masterService.getBrands().then(setMasterBrands).catch(e => console.error('Brands fail', e));
        masterService.getVendors().then(setMasterVendors).catch(e => console.error('Vendors fail', e));
        masterService.getLookups('ASSET_CONDITION').then(setMasterConditions).catch(e => console.error('Conds fail', e));
        masterService.getLookups('ASSET_STATUS').then(setMasterStatuses).catch(e => console.error('Statuses fail', e));
        masterService.getLookups('DISPOSAL_METHOD').then(setMasterDisposalMethods).catch(e => console.error('Disposal fail', e));
        departmentsService.getAll().then(setDepartments).catch(e => console.error('Departments fail', e));
    };

    /** Re-fetches the current page and tab counts — used after
     * create/update/delete/deploy/etc. The list itself already re-fetches
     * via the shared hook whenever its own state changes; this just covers
     * mutations that don't change tab/search/filter/sort/page. */
    const loadData = async () => {
        await Promise.all([loadAssetsForState(qts.state, new AbortController().signal), loadTabCounts()]);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        const init = async () => {
            try {
                const user = await authService.getProfile();
                setCurrentUser(user);
                await Promise.all([loadStaticData(), loadTabCounts()]);
            } catch (error) {
                console.error('Initialization failed:', error);
            } finally {
                setLoading(false);
            }
        };
        init();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const state = location.state as { editAsset?: Asset };
        if (state?.editAsset) {
            handleOpenEdit(state.editAsset);
            // Clear the state so it doesn't re-open on back navigation
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [location.state, navigate, location.pathname]);

    // Auto-generate asset tag when category changes in create mode
    useEffect(() => {
        if (!editMode && assetFormData.category) {
            assetService.generateNextTag(assetFormData.category)
                .then(res => {
                    handleAssetChange('assetTag', res.assetTag);
                })
                .catch(err => console.error('Failed to generate tag', err));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [assetFormData.category, editMode]);

    const handleOpenCreate = () => {
        setEditMode(false);
        resetAssetForm({
            assetTag: '', name: '', category: '', condition: 'new', status: 'available', acquisitionType: 'purchased',
            brand: '', model: '', serialNumber: '', purchaseDate: '', purchaseCost: '', currency: defaultCurrency, vendor: '',
            receivedFromVendorDate: '', vendorMonthlyRent: '', warrantyExpiry: '', usefulLifeYears: '5',
            salvageValue: '', location: '', notes: '',
            hostname: '', poNumber: '', invoiceNumber: '', costCenter: '', businessOwnerId: '',
            warrantyType: '', warrantyStart: '', maintenanceCycleDays: '',
            originalStatus: 'available', statusChangeReason: '',
        });
        setSelectedImages([]);
        setImagePreviewUrls([]);
        setShowAssetModal(true);
    };

    const handleOpenEdit = (asset: Asset) => {
        setEditMode(true);
        setSelectedAssetId(asset.id);
        resetAssetForm({
            assetTag: asset.assetTag, name: asset.name, category: asset.category, condition: asset.condition,
            status: asset.status, acquisitionType: asset.acquisitionType || 'purchased',
            brand: asset.brand || '', model: asset.model || '', serialNumber: asset.serialNumber || '',
            purchaseDate: asset.purchaseDate?.split('T')[0] || '', purchaseCost: asset.purchaseCost?.toString() || '',
            currency: asset.currency || defaultCurrency,
            vendor: asset.vendor || '', receivedFromVendorDate: asset.receivedFromVendorDate?.split('T')[0] || '',
            vendorMonthlyRent: asset.vendorMonthlyRent?.toString() || '',
            warrantyExpiry: asset.warrantyExpiry?.split('T')[0] || '',
            usefulLifeYears: asset.usefulLifeYears?.toString() || '5', salvageValue: asset.salvageValue?.toString() || '',
            location: asset.location || '', notes: asset.notes || '',
            hostname: asset.hostname || '', poNumber: asset.poNumber || '',
            invoiceNumber: asset.invoiceNumber || '', costCenter: asset.costCenter || '',
            businessOwnerId: asset.businessOwnerId?.toString() || '',
            warrantyType: asset.warrantyType || '',
            warrantyStart: asset.warrantyStart?.split('T')[0] || '',
            maintenanceCycleDays: asset.maintenanceCycleDays?.toString() || '',
            originalStatus: asset.status, statusChangeReason: '',
        });
        setSelectedImages([]);
        setImagePreviewUrls([]);
        setShowAssetModal(true);
    };

    const handleViewDetails = (id: number) => {
        navigate(`/dashboard/assets/${id}`);
    };

    const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (!files) return;
        const newFiles = Array.from(files).filter(f => f.type.startsWith('image/'));
        if (newFiles.length === 0) return;
        setSelectedImages(prev => [...prev, ...newFiles]);
        const newUrls = newFiles.map(f => URL.createObjectURL(f));
        setImagePreviewUrls(prev => [...prev, ...newUrls]);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const removeSelectedImage = (index: number) => {
        URL.revokeObjectURL(imagePreviewUrls[index]);
        setSelectedImages(prev => prev.filter((_, i) => i !== index));
        setImagePreviewUrls(prev => prev.filter((_, i) => i !== index));
    };

    const handleSubmitAsset = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!validateAssetForm()) {
            // After the error state renders, move focus (and scroll) to the
            // first invalid control — FormField stamps aria-invalid on it.
            setTimeout(() => {
                const el = document.querySelector<HTMLElement>('form [aria-invalid="true"]');
                el?.scrollIntoView({ block: 'center', behavior: 'smooth' });
                el?.focus({ preventScroll: true });
            }, 50);
            return;
        }

        try {
            setSubmittingAsset(true);
            const isRented = assetFormData.acquisitionType === 'rented';
            const isPurchased = assetFormData.acquisitionType === 'purchased';

            const { originalStatus, statusChangeReason, ...formFields } = assetFormData;
            const payload = {
                ...formFields,
                // Only travels with a real status override; the server rejects
                // the change without it (and ignores it when nothing moved).
                ...(isStatusOverridden ? { statusChangeReason: statusChangeReason.trim() } : {}),
                // Shared fields cleanup
                brand: assetFormData.brand || null,
                model: assetFormData.model || null,
                serialNumber: assetFormData.serialNumber || null,
                vendor: assetFormData.vendor || null,
                location: assetFormData.location || null,
                notes: assetFormData.notes || null,
                warrantyExpiry: assetFormData.warrantyExpiry || null,
                performedBy: currentUser?.id ? Number(currentUser.id) : undefined,

                // New fields
                hostname: assetFormData.hostname || null,
                poNumber: assetFormData.poNumber || null,
                invoiceNumber: assetFormData.invoiceNumber || null,
                costCenter: assetFormData.costCenter || null,
                businessOwnerId: assetFormData.businessOwnerId ? parseInt(assetFormData.businessOwnerId) : null,
                warrantyType: assetFormData.warrantyType || null,
                warrantyStart: assetFormData.warrantyStart || null,
                maintenanceCycleDays: assetFormData.maintenanceCycleDays ? parseInt(assetFormData.maintenanceCycleDays) : null,

                // Purchase-only fields (clear if rented)
                purchaseDate: isPurchased ? (assetFormData.purchaseDate || null) : null,
                purchaseCost: isPurchased ? (assetFormData.purchaseCost ? parseFloat(assetFormData.purchaseCost) : null) : null,
                currency: assetFormData.currency || defaultCurrency,
                // Useful Life (Years) applies to both acquisition types — for
                // rented assets it's measured from Rental Start Date instead
                // of Purchase Date (see Near/Beyond Useful Life KPI).
                usefulLifeYears: assetFormData.usefulLifeYears ? parseInt(assetFormData.usefulLifeYears) : 3,
                salvageValue: isPurchased ? (assetFormData.salvageValue ? parseFloat(assetFormData.salvageValue) : null) : null,

                // Rental-only fields (clear if purchased)
                vendorMonthlyRent: isRented ? (assetFormData.vendorMonthlyRent ? parseFloat(assetFormData.vendorMonthlyRent) : null) : null,
                receivedFromVendorDate: assetFormData.receivedFromVendorDate || null,
            };
            if (editMode && selectedAssetId) {
                await assetService.updateAsset(selectedAssetId, payload);
                if (selectedImages.length > 0) {
                    const formData = new FormData();
                    selectedImages.forEach(f => formData.append('photos', f));
                    await assetService.uploadAssetPhotos(selectedAssetId, formData);
                }
                showToast('Asset updated successfully', 'success');
            } else {
                const created = await assetService.createAsset(payload);
                if (selectedImages.length > 0) {
                    const formData = new FormData();
                    selectedImages.forEach(f => formData.append('photos', f));
                    await assetService.uploadAssetPhotos(created.id, formData);
                }
                showToast('Asset created successfully', 'success');
            }
            setSelectedImages([]);
            setImagePreviewUrls([]);
            setShowAssetModal(false);
            loadData();
        } catch (err) {
            // Surface the server's own message — status-override rejections
            // ("undeploy it first", "a reason is required") are actionable and
            // were previously hidden behind the generic failure text.
            showToast(getErrorMessage(err, 'Failed to save asset'), 'error');
        }
        finally { setSubmittingAsset(false); }
    };

    const handleDelete = (id: number) => {
        setConfirmState({
            show: true, title: 'Delete Asset',
            message: 'Are you sure you want to delete this asset? Assets with assignment history cannot be deleted and must be disposed instead.',
            type: 'danger',
            onConfirm: async () => {
                try { await assetService.deleteAsset(id); showToast('Asset deleted', 'success'); loadData(); }
                catch (err: any) { showToast(err?.response?.data?.message || 'Failed to delete asset', 'error'); }
                setConfirmState(prev => ({ ...prev, show: false }));
            },
        });
    };

    const handleOpenDeploy = (id: number) => {
        setSelectedAssetId(id);
        const asset = assets.find(a => a.id === id);
        const category = masterCategories.find(c => c.name.toLowerCase() === asset?.category?.toLowerCase());
        const allowedTargets = category?.allowedTargetTypes && category.allowedTargetTypes.length > 0
            ? category.allowedTargetTypes
            : ['PERSON'];
        setAllowedTargetTypes(allowedTargets);

        resetDeployForm({
            targetType: allowedTargets[0] || 'PERSON',
            userId: '',
            location: asset?.location || '',
            site: asset?.site || '',
            building: asset?.building || '',
            floor: asset?.floor || '',
            roomDesk: asset?.roomDesk || '',
            deploymentDate: new Date().toISOString().split('T')[0],
            reason: ''
        });
        setShowDeployModal(true);
    };

    const handleDeploy = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedAssetId || !validateDeployForm()) return;
        try {
            setSubmittingAction(true);
            const payload = {
                ...deployFormData,
                userId: deployFormData.userId ? parseInt(deployFormData.userId) : undefined,
                performedBy: currentUser?.id ? Number(currentUser.id) : undefined
            };
            await assetService.deployAsset(selectedAssetId, payload);
            showToast('Asset deployed successfully', 'success');
            setShowDeployModal(false); loadData();
        } catch (error: any) {
            // Server-side validation wins: if it rejected specific fields, show
            // the messages on those fields rather than a generic toast. The
            // modal stays open so the user keeps their input.
            if (error?.fieldErrors) {
                applyDeployServerErrors(error.fieldErrors);
            }
            showToast(error?.friendlyMessage || 'Failed to deploy asset', 'error');
        }
        finally { setSubmittingAction(false); }
    };

    const handleOpenUndeploy = (id: number) => {
        setSelectedAssetId(id);
        resetUndeployForm({ reason: '', condition: '' });
        setShowUndeployModal(true);
    };

    const handleUndeploy = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedAssetId || !validateUndeployForm()) return;
        try {
            setSubmittingAction(true);
            await assetService.undeployAsset(selectedAssetId, undeployFormData.reason, undeployFormData.condition, currentUser?.id ? Number(currentUser.id) : undefined);
            showToast('Asset undeployed', 'success');
            setShowUndeployModal(false); loadData();
        } catch { showToast('Failed to undeploy', 'error'); }
        finally { setSubmittingAction(false); }
    };

    const handleOpenMaintenance = (id: number) => {
        setSelectedAssetId(id);
        resetMaintenanceForm({ lastMaintenanceDate: '', nextMaintenanceDate: '', maintenanceNotes: '' });
        setShowMaintenanceModal(true);
    };

    const handleScheduleMaintenance = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedAssetId || !validateMaintenanceForm()) return;
        try {
            setSubmittingAction(true);
            await assetService.scheduleMaintenance(selectedAssetId, maintenanceFormData, currentUser?.id ? Number(currentUser.id) : undefined);
            showToast('Maintenance scheduled', 'success');
            setShowMaintenanceModal(false); loadData();
        } catch { showToast('Failed to schedule maintenance', 'error'); }
        finally { setSubmittingAction(false); }
    };

    const handleOpenCompleteMaintenance = (id: number) => {
        setSelectedAssetId(id);
        resetMaintenanceCompleteForm({
            maintenanceCompletedDate: new Date().toISOString().split('T')[0],
            workPerformed: '', completionNotes: '', nextMaintenanceDate: '',
        });
        setShowMaintenanceCompleteModal(true);
    };

    const handleSubmitMaintenanceComplete = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedAssetId || !validateMaintenanceCompleteForm()) return;
        try {
            setSubmittingAction(true);
            const payload = {
                ...maintenanceCompleteFormData,
                completionNotes: maintenanceCompleteFormData.completionNotes?.trim() || undefined,
                nextMaintenanceDate: maintenanceCompleteFormData.nextMaintenanceDate || undefined,
            };
            await assetService.completeMaintenance(selectedAssetId, payload, currentUser?.id ? Number(currentUser.id) : undefined);
            showToast('Maintenance completed', 'success');
            setShowMaintenanceCompleteModal(false); loadData();
        } catch { showToast('Failed to complete maintenance', 'error'); }
        finally { setSubmittingAction(false); }
    };

    const handleOpenDisposal = (id: number) => {
        setSelectedAssetId(id);
        resetDisposalForm({ disposalDate: '', disposalMethod: '', disposalReason: '', disposalNotes: '' });
        setShowDisposalModal(true);
    };

    const handleDispose = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedAssetId || !validateDisposalForm()) return;
        try {
            setSubmittingAction(true);
            await assetService.disposeAsset(selectedAssetId, disposalFormData, currentUser?.id ? Number(currentUser.id) : undefined);
            showToast('Asset disposed', 'success');
            setShowDisposalModal(false); loadData();
        } catch { showToast('Failed to dispose asset', 'error'); }
        finally { setSubmittingAction(false); }
    };

    const getStatusBadge = (status: string) => {
        const map: Record<string, { variant: 'default' | 'success' | 'warning' | 'destructive' | 'secondary' | 'outline'; label: string }> = {
            available: { variant: 'success', label: 'Available' },
            deployed: { variant: 'default', label: 'Deployed' },
            maintenance: { variant: 'warning', label: 'Maintenance' },
            repair: { variant: 'warning', label: 'Repair' },
            'in repair': { variant: 'warning', label: 'In Repair' },
            disposed: { variant: 'secondary', label: 'Disposed' },
            retired: { variant: 'secondary', label: 'Retired' },
            lost: { variant: 'destructive', label: 'Lost' },
            stolen: { variant: 'destructive', label: 'Stolen' },
        };
        const s = map[status.toLowerCase()] || { variant: 'outline', label: status };
        return <Badge variant={s.variant}>{s.label}</Badge>;
    };

    const getDeployLabel = (categoryName: string) => {
        const category = masterCategories.find(c => c.name.toLowerCase() === categoryName?.toLowerCase());
        const allowed = category?.allowedTargetTypes || ['PERSON'];
        if (allowed.length === 1 && allowed.includes('PERSON')) return 'Deploy to User';
        if (allowed.length === 1 && allowed.includes('LOCATION')) return 'Deploy to Location';
        return 'Deploy Asset';
    };

    // Filtering, sorting, and pagination all happen server-side now (see
    // loadAssets) — `assets` already holds exactly the current page for the
    // active tab/search/advanced filters/sort, and `totalAssets` is the
    // server-side count across every matching row (not just this page).
    const paginatedAssets = assets;
    const advancedFilters = qts.state.filters;

    const removeFromArrayFilter = <K extends keyof AdvancedFilterState>(key: K, value: string | number) => {
        const arr = (advancedFilters[key] as any[] | undefined)?.filter((v) => v !== value);
        qts.setFilters({ ...advancedFilters, [key]: arr && arr.length ? arr : undefined });
    };

    const clearFilterKeys = (...keys: (keyof AdvancedFilterState)[]) => {
        const next = { ...advancedFilters };
        keys.forEach((k) => { delete next[k]; });
        qts.setFilters(next);
    };

    // Removable chips shown above the table for every active advanced filter —
    // one per selected value for multi-selects, one per range for range fields.
    const filterChips: FilterChip[] = useMemo(() => {
        const chips: FilterChip[] = [];

        (advancedFilters.category ?? []).forEach(v => chips.push({ key: `category-${v}`, label: `Category: ${v}`, onRemove: () => removeFromArrayFilter('category', v) }));
        (advancedFilters.brand ?? []).forEach(v => chips.push({ key: `brand-${v}`, label: `Brand: ${v}`, onRemove: () => removeFromArrayFilter('brand', v) }));
        (advancedFilters.vendor ?? []).forEach(v => chips.push({ key: `vendor-${v}`, label: `Vendor: ${v}`, onRemove: () => removeFromArrayFilter('vendor', v) }));
        (advancedFilters.status ?? []).forEach(v => chips.push({
            key: `status-${v}`,
            label: `Status: ${masterStatuses.find(s => s.value === v)?.label || v}`,
            onRemove: () => removeFromArrayFilter('status', v),
        }));
        (advancedFilters.condition ?? []).forEach(v => chips.push({
            key: `condition-${v}`,
            label: `Condition: ${masterConditions.find(c => c.value === v)?.label || v}`,
            onRemove: () => removeFromArrayFilter('condition', v),
        }));
        (advancedFilters.acquisitionType ?? []).forEach(v => chips.push({
            key: `acq-${v}`,
            label: `Acquisition: ${v.charAt(0).toUpperCase()}${v.slice(1)}`,
            onRemove: () => removeFromArrayFilter('acquisitionType', v),
        }));
        (advancedFilters.assignedToId ?? []).forEach(id => {
            const u = users.find(u => u.id === id);
            chips.push({ key: `assignedTo-${id}`, label: `Assigned To: ${u ? `${u.firstName} ${u.lastName}` : id}`, onRemove: () => removeFromArrayFilter('assignedToId', id) });
        });
        (advancedFilters.businessOwnerId ?? []).forEach(id => {
            const u = users.find(u => u.id === id);
            chips.push({ key: `owner-${id}`, label: `Business Owner: ${u ? `${u.firstName} ${u.lastName}` : id}`, onRemove: () => removeFromArrayFilter('businessOwnerId', id) });
        });
        (advancedFilters.departmentId ?? []).forEach(id => {
            const d = departments.find(d => d.id === id);
            chips.push({ key: `dept-${id}`, label: `Department: ${d?.name || id}`, onRemove: () => removeFromArrayFilter('departmentId', id) });
        });
        if (advancedFilters.model) chips.push({ key: 'model', label: `Model: ${advancedFilters.model}`, onRemove: () => clearFilterKeys('model') });
        if (advancedFilters.location) chips.push({ key: 'location', label: `Location: ${advancedFilters.location}`, onRemove: () => clearFilterKeys('location') });
        if (advancedFilters.costCenter) chips.push({ key: 'costCenter', label: `Cost Center: ${advancedFilters.costCenter}`, onRemove: () => clearFilterKeys('costCenter') });
        if (advancedFilters.purchaseDateFrom || advancedFilters.purchaseDateTo) {
            chips.push({
                key: 'purchaseDateRange',
                label: `Purchase Date: ${advancedFilters.purchaseDateFrom || '…'} – ${advancedFilters.purchaseDateTo || '…'}`,
                onRemove: () => clearFilterKeys('purchaseDateFrom', 'purchaseDateTo'),
            });
        }
        if (advancedFilters.warrantyExpiryFrom || advancedFilters.warrantyExpiryTo) {
            chips.push({
                key: 'warrantyExpiryRange',
                label: `Warranty Expiry: ${advancedFilters.warrantyExpiryFrom || '…'} – ${advancedFilters.warrantyExpiryTo || '…'}`,
                onRemove: () => clearFilterKeys('warrantyExpiryFrom', 'warrantyExpiryTo'),
            });
        }
        if (advancedFilters.purchaseCostMin !== undefined || advancedFilters.purchaseCostMax !== undefined) {
            chips.push({
                key: 'purchaseCostRange',
                label: `Purchase Cost: ${advancedFilters.purchaseCostMin ?? '…'} – ${advancedFilters.purchaseCostMax ?? '…'}`,
                onRemove: () => clearFilterKeys('purchaseCostMin', 'purchaseCostMax'),
            });
        }
        if (advancedFilters.monthlyRentMin !== undefined || advancedFilters.monthlyRentMax !== undefined) {
            chips.push({
                key: 'monthlyRentRange',
                label: `Monthly Rent: ${advancedFilters.monthlyRentMin ?? '…'} – ${advancedFilters.monthlyRentMax ?? '…'}`,
                onRemove: () => clearFilterKeys('monthlyRentMin', 'monthlyRentMax'),
            });
        }

        return chips;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [advancedFilters, users, departments, masterStatuses, masterConditions]);

    // Filter-option lists for the drawer — active master-data values only, so
    // the dropdowns never offer a value that can no longer be assigned (an
    // asset already carrying an inactive value still displays fine — it just
    // isn't offered as a new filter choice).
    const categoryFilterOptions: MultiSelectOption[] = useMemo(
        () => masterCategories.filter(c => c.isActive !== false).map(c => ({ value: c.name, label: c.name })),
        [masterCategories]
    );
    const brandFilterOptions: MultiSelectOption[] = useMemo(
        () => masterBrands.filter(b => b.isActive !== false).map(b => ({ value: b.name, label: b.name })),
        [masterBrands]
    );
    const vendorFilterOptions: MultiSelectOption[] = useMemo(
        () => masterVendors.filter(v => v.isActive !== false).map(v => ({ value: v.name, label: v.name })),
        [masterVendors]
    );
    const statusFilterOptions: MultiSelectOption[] = useMemo(
        () => masterStatuses.filter(s => s.isActive !== false).map(s => ({ value: s.value, label: s.label })),
        [masterStatuses]
    );
    const conditionFilterOptions: MultiSelectOption[] = useMemo(
        () => masterConditions.filter(c => c.isActive !== false).map(c => ({ value: c.value, label: c.label })),
        [masterConditions]
    );

    // Status is only editable when editing an existing asset (a new one always
    // starts Available) and never once it has been disposed, which is terminal.
    const canOverrideStatus = editMode && assetFormData.originalStatus !== 'disposed';
    const isStatusOverridden = canOverrideStatus && assetFormData.status !== assetFormData.originalStatus;
    // The asset's own status stays selectable so the change can be undone
    // before saving, even though it isn't a manual status itself.
    const statusOverrideOptions = useMemo(() => {
        // A lost or stolen asset that turns up again goes back to Available,
        // unassigned — otherwise marking it would be a one-way door.
        const recoverable = ['lost', 'stolen'].includes(assetFormData.originalStatus);
        const manual = [...MANUALLY_SETTABLE_STATUSES, ...(recoverable ? ['available'] : [])].filter(v => {
            if (v === assetFormData.originalStatus) return false;
            // A status an admin has deactivated in Asset Setup is not offered.
            const master = masterStatuses.find(s => s.value === v);
            return !master || master.isActive !== false;
        });
        return [assetFormData.originalStatus, ...manual].map(value => {
            const master = masterStatuses.find(s => s.value === value);
            return { value, label: master?.label || value };
        });
    }, [assetFormData.originalStatus, masterStatuses]);
    const departmentFilterOptions: MultiSelectOption[] = useMemo(
        () => departments.filter(d => d.isActive !== false).map(d => ({ value: String(d.id), label: d.name })),
        [departments]
    );
    const userFilterOptions: MultiSelectOption[] = useMemo(
        () => users.map(u => ({ value: String(u.id), label: `${u.firstName} ${u.lastName}` })),
        [users]
    );

    const handleExportCSV = async () => {
        let exportAssets: Asset[];
        try {
            const result = await assetService.getAssets(buildListFilters(true));
            exportAssets = result.data;
        } catch {
            showToast('Failed to prepare export', 'error');
            return;
        }
        if (exportAssets.length === 0) {
            showToast('No data to export', 'warning');
            return;
        }

        const headers = [
            'Asset Tag', 'Name', 'Category', 'Brand', 'Model', 'Status', 'Condition',
            'Assigned To', 'Purchase Cost', 'Monthly Rent', 'Acquisition Type', 'Purchase Date', 'Warranty Expiry'
        ];

        const rows = exportAssets.map(asset => [
            asset.assetTag,
            asset.name,
            asset.category,
            asset.brand || '-',
            asset.model || '-',
            asset.status,
            asset.condition,
            deploymentTargetLabel(asset),
            asset.purchaseCost || 0,
            asset.vendorMonthlyRent || 0,
            asset.acquisitionType || 'Purchased',
            asset.purchaseDate ? new Date(asset.purchaseDate).toLocaleDateString() : '-',
            asset.warrantyExpiry ? new Date(asset.warrantyExpiry).toLocaleDateString() : '-'
        ]);

        exportToCsv(`assets_report_${new Date().toISOString().split('T')[0]}.csv`, headers, rows);
        showToast('Assets exported to CSV successfully', 'success');
    };

    const handleImportClick = () => {
        importInputRef.current?.click();
    };

    const handleFileImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (!file.name.endsWith('.xlsx')) {
            showToast('Please upload an Excel (.xlsx) file', 'error');
            return;
        }

        try {
            setLoading(true);
            const result = await assetService.validateImport(file);
            setImportPreviewData(result.data);
            setImportPreviewSummary(result.summary);
            setTakenAssetTagKeys(result.takenAssetTagKeys ?? []);
            setShowImportPreviewModal(true);
        } catch (error: unknown) {
            const err = error as { response?: { data?: { message?: string } }, message?: string };
            showToast('Validation failed: ' + (err.response?.data?.message || err.message), 'error');
        } finally {
            setLoading(false);
            if (importInputRef.current) importInputRef.current.value = '';
        }
    };

    const handleConfirmImport = async () => {
        try {
            setIsImporting(true);
            // WARNING rows (possible duplicates, hostname reuse) are importable;
            // only ERROR rows are excluded. The backend re-validates and
            // re-checks duplicates on every row it receives regardless.
            const validAssets = importPreviewData.filter(a => a._status !== 'ERROR');
            if (validAssets.length === 0) {
                showToast('No valid assets to import', 'error');
                return;
            }

            const result = await assetService.confirmImport(validAssets, currentUser?.id ? Number(currentUser.id) : undefined);
            
            if (result.success > 0) {
                showToast(`Successfully imported ${result.success} assets`, 'success');
            }
            if (result.failed > 0) {
                showToast(`Failed to import ${result.failed} assets.`, 'warning');
                console.error('Import Errors:', result.errors);
            }
            
            setShowImportPreviewModal(false);
            loadData();
        } catch (error: unknown) {
            const err = error as { response?: { data?: { message?: string } }, message?: string };
            showToast('Import failed: ' + (err.response?.data?.message || err.message), 'error');
        } finally {
            setIsImporting(false);
        }
    };

    const handleDownloadTemplate = async () => {
        try {
            await assetService.downloadImportTemplate();
        } catch (error: unknown) {
            const err = error as { response?: { data?: { message?: string } }, message?: string };
            showToast('Failed to download template: ' + (err.response?.data?.message || err.message), 'error');
        }
    };

    // Badge counts come from the dedicated statistics endpoint (permission-scoped,
    // like the list itself) rather than being derived from the fetched page —
    // the page now only ever holds one page's worth of rows.
    const tabs: { key: TabKey; label: string; icon: React.ReactNode; count: number }[] = [
        { key: 'all', label: 'All Assets', icon: <Monitor className="h-4 w-4" />, count: tabCounts?.total ?? 0 },
        { key: 'available', label: 'Available', icon: <Check className="h-4 w-4" />, count: tabCounts?.available ?? 0 },
        { key: 'deployed', label: 'Deployed', icon: <User className="h-4 w-4" />, count: tabCounts?.deployed ?? 0 },
        { key: 'maintenance', label: 'Maintenance', icon: <Wrench className="h-4 w-4" />, count: tabCounts?.maintenance ?? 0 },
        { key: 'disposed', label: 'Disposed', icon: <Recycle className="h-4 w-4" />, count: tabCounts?.decommissioned ?? 0 },
    ];

    if (loading) return (
        <div className="flex items-center justify-center h-64">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
    );

    return (
        <div className="space-y-6">
            <PageHeader title="Asset Management" description="Asset Lifecycle">
                <div className="flex items-center gap-2">
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button variant="outline" className="gap-2">
                                <Settings2 className="h-4 w-4" /> Manage Data
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-56">
                            <DropdownMenuItem onClick={handleExportCSV} className="gap-2">
                                <FileSpreadsheet className="h-4 w-4 text-success" />
                                Export to Excel (.csv)
                            </DropdownMenuItem>
                            {currentUser?.role?.name === 'Admin' && (
                                <>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={handleDownloadTemplate} className="gap-2">
                                        <Download className="h-4 w-4 text-primary" />
                                        Download Import Template
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={handleImportClick} className="gap-2">
                                        <Upload className="h-4 w-4 text-primary" />
                                        Import Data
                                    </DropdownMenuItem>
                                </>
                            )}
                        </DropdownMenuContent>
                    </DropdownMenu>

                    {currentUser?.role?.name === 'Admin' && (
                        <>
                            <input
                                type="file"
                                ref={importInputRef}
                                className="hidden"
                                accept=".xlsx"
                                onChange={handleFileImport}
                            />
                            <Button onClick={handleOpenCreate}><Plus className="h-4 w-4 mr-2" />Add Asset</Button>
                        </>
                    )}
                </div>
            </PageHeader>

            <Card>
                <CardContent className="p-0">
                    <ListTabBar>
                        <Tabs value={qts.state.tab} onValueChange={(v) => qts.setTab(v)}>
                            <TabsList className="bg-transparent h-auto p-0 gap-8 border-none flex justify-start">
                                {tabs.map((tab) => (
                                    <TabsTrigger
                                        key={tab.key}
                                        value={tab.key}
                                        className="group relative data-[state=active]:text-primary data-[state=active]:shadow-none rounded-none px-0 pb-3 pt-4 bg-transparent whitespace-nowrap gap-2 text-muted-foreground hover:text-foreground transition-all border-none"
                                    >
                                        <div className="flex items-center gap-1.5">
                                            {tab.icon}
                                            <span className="font-semibold text-sm tracking-tight">{tab.label}</span>
                                            <span className="ml-0.5 px-2 py-0.5 rounded-full bg-muted text-[10px] font-bold group-data-[state=active]:bg-primary/10 group-data-[state=active]:text-primary transition-colors">
                                                {tab.count}
                                            </span>
                                        </div>
                                        {qts.state.tab === tab.key && (
                                            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary animate-in fade-in slide-in-from-bottom-1" />
                                        )}
                                    </TabsTrigger>
                                ))}
                            </TabsList>
                        </Tabs>
                    </ListTabBar>

                    <ListToolbar
                        searchValue={qts.searchInput}
                        onSearchChange={qts.setSearchInput}
                        searchPlaceholder="Search assets..."
                        activeFilterCount={qts.activeFilterCount}
                        onOpenFilters={() => { qts.syncDraft(); setShowFilters(true); }}
                        onClearAll={qts.clearSearchAndFilters}
                        hasActive={qts.hasActiveSearchOrFilters}
                    />

                    <FilterChipsBar chips={filterChips} onClearAll={qts.clearFilters} />

                    <TableLoadingBar show={listLoading} />

                    {/* Mobile (<md): stacked card list — tap a card for full details */}
                    <div className={cn("md:hidden divide-y divide-border", listLoading && TABLE_DIM_CLASS)}>
                        {paginatedAssets.length === 0 ? (
                            <EmptyAssetsState hasFilters={qts.hasActiveSearchOrFilters} onClearFilters={qts.clearSearchAndFilters} />
                        ) : (
                            paginatedAssets.map((asset) => (
                                <div
                                    key={asset.id}
                                    className="flex items-start gap-3 px-4 py-3 active:bg-accent/50 cursor-pointer"
                                    onClick={() => handleViewDetails(asset.id)}
                                >
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-2">
                                            <code className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-xs">{asset.assetTag}</code>
                                            {getStatusBadge(asset.status)}
                                        </div>
                                        <p className="mt-1 truncate font-medium">{asset.name}</p>
                                        <p className="truncate text-xs text-muted-foreground">
                                            {asset.category}
                                            {asset.brand || asset.model ? ` · ${asset.brand ?? ''} ${asset.model ?? ''}`.trimEnd() : ''}
                                        </p>
                                        <p className="mt-0.5 truncate text-xs text-muted-foreground">
                                            {deploymentTargetLabel(asset) === 'Unassigned'
                                                ? 'Unassigned'
                                                : `Assigned to ${deploymentTargetLabel(asset)}`}
                                            {asset.purchaseCost ? ` · ${formatCost(Number(asset.purchaseCost), asset.currency)}` : ''}
                                        </p>
                                    </div>
                                    <div
                                        className="flex shrink-0 items-center"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        {currentUser?.role?.name === 'Admin' && (
                                            <ActionDropdown
                                                actions={[
                                                    ...(asset.status === 'maintenance' || asset.status === 'repair' ? [
                                                        // Edit stays available here too: an asset under maintenance
                                                        // still needs correcting, and it is the only route to the
                                                        // manual Lost/Stolen/Retired override (Story 74).
                                                        { label: 'Edit', icon: <Edit className="h-4 w-4" />, onClick: () => handleOpenEdit(asset), variant: 'default' as const },
                                                        { label: 'Complete Maintenance', icon: <Check className="h-4 w-4" />, onClick: () => handleOpenCompleteMaintenance(asset.id), variant: 'success' as const },
                                                        { label: 'Dispose Asset', icon: <Recycle className="h-4 w-4" />, onClick: () => handleOpenDisposal(asset.id), variant: 'default' as const },
                                                    ] : [
                                                        { label: 'Edit', icon: <Edit className="h-4 w-4" />, onClick: () => handleOpenEdit(asset), variant: 'default' as const },
                                                        ...(asset.status === 'available' ? [{ label: getDeployLabel(asset.category), icon: <User className="h-4 w-4" />, onClick: () => handleOpenDeploy(asset.id), variant: 'success' as const }] : []),
                                                        ...(asset.status === 'deployed' ? [{ label: 'Undeploy', icon: <User className="h-4 w-4" />, onClick: () => handleOpenUndeploy(asset.id), variant: 'default' as const }] : []),
                                                        ...(canDispose(asset.status) ? [{ label: 'Dispose Asset', icon: <Recycle className="h-4 w-4" />, onClick: () => handleOpenDisposal(asset.id), variant: 'default' as const }] : []),
                                                    ]),
                                                ] as ActionItem[]}
                                            />
                                        )}
                                    </div>
                                </div>
                            ))
                        )}
                    </div>

                    {/* Desktop (md+): full data table */}
                    <div className={cn("hidden md:block overflow-x-auto", listLoading && TABLE_DIM_CLASS)}>
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="cursor-pointer hover:text-primary transition-colors" onClick={() => qts.toggleSort('assetTag')}>
                                        <div className="flex items-center gap-1">Asset Tag <ArrowUpDown className="h-3 w-3" /></div>
                                    </TableHead>
                                    <TableHead className="cursor-pointer hover:text-primary transition-colors" onClick={() => qts.toggleSort('name')}>
                                        <div className="flex items-center gap-1">Name <ArrowUpDown className="h-3 w-3" /></div>
                                    </TableHead>
                                    <TableHead className="cursor-pointer hover:text-primary transition-colors" onClick={() => qts.toggleSort('category')}>
                                        <div className="flex items-center gap-1">Category <ArrowUpDown className="h-3 w-3" /></div>
                                    </TableHead>
                                    <TableHead className="cursor-pointer hover:text-primary transition-colors" onClick={() => qts.toggleSort('status')}>
                                        <div className="flex items-center gap-1">Status <ArrowUpDown className="h-3 w-3" /></div>
                                    </TableHead>
                                    <TableHead className="cursor-pointer hover:text-primary transition-colors" onClick={() => qts.toggleSort('assignedTo')}>
                                        <div className="flex items-center gap-1">Assigned To / Location <ArrowUpDown className="h-3 w-3" /></div>
                                    </TableHead>
                                    <TableHead>Photos</TableHead>
                                    <TableHead className="cursor-pointer hover:text-primary transition-colors" onClick={() => qts.toggleSort('purchaseCost')}>
                                        <div className="flex items-center gap-1">Purchase Cost <ArrowUpDown className="h-3 w-3" /></div>
                                    </TableHead>
                                    <TableHead className="cursor-pointer hover:text-primary transition-colors" onClick={() => qts.toggleSort('vendorMonthlyRent')}>
                                        <div className="flex items-center gap-1">Monthly Rent <ArrowUpDown className="h-3 w-3" /></div>
                                    </TableHead>
                                    <TableHead className="w-[60px]"></TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {paginatedAssets.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={9} className="py-8">
                                            <EmptyAssetsState hasFilters={qts.hasActiveSearchOrFilters} onClearFilters={qts.clearSearchAndFilters} />
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    paginatedAssets.map((asset) => (
                                        <TableRow key={asset.id}>
                                            <TableCell><code className="text-sm bg-muted px-1.5 py-0.5 rounded">{asset.assetTag}</code></TableCell>
                                            <TableCell>
                                                <div className="font-medium">{asset.name}</div>
                                                <div className="text-xs text-muted-foreground">{asset.brand} {asset.model}</div>
                                            </TableCell>
                                            <TableCell><Badge variant="info">{asset.category}</Badge></TableCell>
                                            <TableCell>{getStatusBadge(asset.status)}</TableCell>
                                            <TableCell>
                                                {(() => {
                                                    const target = deploymentTarget(asset);
                                                    if (target.kind === 'none') {
                                                        return <span className="text-muted-foreground text-sm">Unassigned</span>;
                                                    }
                                                    return (
                                                        <div>
                                                            <div className="flex items-center gap-1.5 font-medium text-sm">
                                                                {target.kind === 'location' && <MapPin className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />}
                                                                {target.name}
                                                            </div>
                                                            {target.detail && (
                                                                <div className="text-xs text-muted-foreground">{target.detail}</div>
                                                            )}
                                                        </div>
                                                    );
                                                })()}
                                            </TableCell>
                                            <TableCell>
                                                <span className="flex items-center gap-1 text-muted-foreground text-sm">
                                                    <ImageIcon className="h-3.5 w-3.5" />{assetPhotos[asset.id] || 0}
                                                </span>
                                            </TableCell>
                                            <TableCell className="text-sm">{asset.purchaseCost ? formatCost(Number(asset.purchaseCost), asset.currency) : '-'}</TableCell>
                                            <TableCell className="text-sm">{asset.vendorMonthlyRent ? formatCost(Number(asset.vendorMonthlyRent), asset.currency) : '-'}</TableCell>
                                            <TableCell>
                                                <div className="flex items-center gap-1">
                                                    <Button variant="ghost" size="icon-sm" onClick={() => handleViewDetails(asset.id)} title="View Details">
                                                        <Eye className="h-4 w-4" />
                                                    </Button>
                                                    {currentUser?.role?.name === 'Admin' && (
                                                        <ActionDropdown
                                                            actions={[
                                                                ...(asset.status === 'maintenance' || asset.status === 'repair' ? [
                                                                    { label: 'Edit', icon: <Edit className="h-4 w-4" />, onClick: () => handleOpenEdit(asset), variant: 'default' as const },
                                                                    { label: 'Complete Maintenance', icon: <Check className="h-4 w-4" />, onClick: () => handleOpenCompleteMaintenance(asset.id), variant: 'success' as const },
                                                                    { label: 'Dispose Asset', icon: <Recycle className="h-4 w-4" />, onClick: () => handleOpenDisposal(asset.id), variant: 'default' as const },
                                                                ] : [
                                                                    { label: 'Edit', icon: <Edit className="h-4 w-4" />, onClick: () => handleOpenEdit(asset), variant: 'default' as const },
                                                                    ...(asset.status === 'available' ? [{ label: getDeployLabel(asset.category), icon: <User className="h-4 w-4" />, onClick: () => handleOpenDeploy(asset.id), variant: 'success' as const }] : []),
                                                                    ...(asset.status === 'deployed' ? [{ label: 'Undeploy', icon: <User className="h-4 w-4" />, onClick: () => handleOpenUndeploy(asset.id), variant: 'default' as const }] : []),
                                                                    ...(asset.status !== 'disposed' && asset.status !== 'maintenance' && asset.status !== 'repair' ? [
                                                                        { label: 'Schedule Maintenance', icon: <Wrench className="h-4 w-4" />, onClick: () => handleOpenMaintenance(asset.id), variant: 'default' as const },
                                                                    ] : []),
                                                                    ...(canDispose(asset.status) ? [
                                                                        { label: 'Dispose Asset', icon: <Recycle className="h-4 w-4" />, onClick: () => handleOpenDisposal(asset.id), variant: 'default' as const },
                                                                    ] : []),
                                                                ]),
                                                                { 
                                                                    label: 'Delete', 
                                                                    icon: <Trash2 className="h-4 w-4" />, 
                                                                    onClick: () => handleDelete(asset.id), 
                                                                    variant: 'danger' as const,
                                                                    disabled: !!asset.assignedTo || !!asset.location || !!asset.site
                                                                },
                                                            ] as ActionItem[]}
                                                        />
                                                    )}
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </div>
                    <Pagination
                        currentPage={qts.state.page}
                        totalPages={Math.max(1, Math.ceil(totalAssets / qts.state.pageSize))}
                        onPageChange={qts.setPage}
                        totalItems={totalAssets}
                        pageSize={qts.state.pageSize}
                        onPageSizeChange={qts.setPageSize}
                    />
                </CardContent>
            </Card>

            {/* Asset Create/Edit Dialog */}
            <Dialog open={showAssetModal} onOpenChange={setShowAssetModal}>
                {/* Fixed header + footer; only the form body scrolls */}
                <DialogContent className="sm:max-w-[960px] p-0 sm:p-0 gap-0 flex flex-col overflow-hidden">
                    <DialogHeader className="shrink-0 border-b px-6 py-4">
                        <DialogTitle>{editMode ? 'Edit Asset' : 'New Asset'}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSubmitAsset} className="flex min-h-0 flex-1 flex-col">
                    <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">

                        <section className="space-y-4">
                        <h4 className="text-sm font-semibold">Basic Information</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                            <FormField id="category" label="Category" required error={assetErrors.category} hint="Classification of the asset">
                                <select id="category" className={SELECT_CLS} value={assetFormData.category} onChange={(e) => handleAssetChange('category', e.target.value)} onBlur={() => handleAssetBlur('category')}>
                                    <option value="">Select category...</option>
                                    {/* Inactive master data is not offered; the asset's current value stays listed. */}
                                    {masterCategories
                                        .filter(cat => cat.isActive !== false || cat.name === assetFormData.category)
                                        .map(cat => <option key={cat.id} value={cat.name}>{cat.isActive === false ? `${cat.name} (Inactive)` : cat.name}</option>)}
                                </select>
                            </FormField>
                            <FormField id="assetTag" label="Asset Tag" required error={assetErrors.assetTag} hint="Unique ID (e.g. LAP-001)">
                                <Input value={assetFormData.assetTag} onChange={(e) => handleAssetChange('assetTag', e.target.value)} onBlur={() => handleAssetBlur('assetTag')} placeholder="LAP-001" disabled={!editMode && !!assetFormData.category} />
                            </FormField>
                            <FormField id="name" label="Asset Name" required error={assetErrors.name} hint="Common name of the asset">
                                <Input value={assetFormData.name} onChange={(e) => handleAssetChange('name', e.target.value)} onBlur={() => handleAssetBlur('name')} placeholder="MacBook Pro 16" />
                            </FormField>
                            <FormField id="hostname" label="Hostname" hint="Network identification name">
                                <Input value={assetFormData.hostname} onChange={(e) => handleAssetChange('hostname', e.target.value)} placeholder="IT-LAP-001" />
                            </FormField>
                            <FormField id="brand" label="Brand" hint="Manufacturer name">
                                <select id="brand" className={SELECT_CLS} value={assetFormData.brand} onChange={(e) => handleAssetChange('brand', e.target.value)}>
                                    <option value="">Select brand...</option>
                                    {masterBrands
                                        .filter(b => b.isActive !== false || b.name === assetFormData.brand)
                                        .map(b => <option key={b.id} value={b.name}>{b.isActive === false ? `${b.name} (Inactive)` : b.name}</option>)}
                                </select>
                            </FormField>
                            <FormField id="model" label="Model" hint="Specific product model">
                                <Input value={assetFormData.model} onChange={(e) => handleAssetChange('model', e.target.value)} placeholder="MBP16-2023" />
                            </FormField>
                            <FormField id="serialNumber" label="Serial Number" hint="Manufacturer's unique serial number">
                                <Input value={assetFormData.serialNumber} onChange={(e) => handleAssetChange('serialNumber', e.target.value)} placeholder="C02XYZ123" />
                            </FormField>
                            <FormField id="condition" label="Condition" required error={assetErrors.condition} hint="Physical state of the asset">
                                <select id="condition" className={SELECT_CLS} value={assetFormData.condition} onChange={(e) => handleAssetChange('condition', e.target.value)} onBlur={() => handleAssetBlur('condition')}>
                                    <option value="">Select condition...</option>
                                    {masterConditions.map(cond => <option key={cond.id} value={cond.value}>{cond.label}</option>)}
                                </select>
                            </FormField>
                            <FormField
                                id="status"
                                label="Status"
                                error={assetErrors.status}
                                hint={canOverrideStatus
                                    ? 'Set by the Deploy, Undeploy, Maintenance and Dispose actions — override only to record a loss or retirement'
                                    : 'Current availability status (Automated)'}
                            >
                                {canOverrideStatus ? (
                                    <select
                                        id="status"
                                        className={SELECT_CLS}
                                        value={assetFormData.status}
                                        onChange={(e) => handleAssetChange('status', e.target.value)}
                                        onBlur={() => handleAssetBlur('status')}
                                    >
                                        {statusOverrideOptions.map(s => (
                                            <option key={s.value} value={s.value}>{s.label}</option>
                                        ))}
                                    </select>
                                ) : (
                                    <Input
                                        value={masterStatuses.find(s => s.value === assetFormData.status)?.label || assetFormData.status || 'Available'}
                                        disabled
                                        className="bg-muted cursor-not-allowed font-medium"
                                    />
                                )}
                            </FormField>
                            {isStatusOverridden && (
                                <FormField
                                    id="statusChangeReason"
                                    label="Reason for status change"
                                    required
                                    error={assetErrors.statusChangeReason}
                                    hint="Recorded on the asset's audit history"
                                    className="md:col-span-2"
                                >
                                    <Textarea
                                        id="statusChangeReason"
                                        rows={2}
                                        maxLength={ASSET_STATUS_REASON_MAX_LENGTH}
                                        value={assetFormData.statusChangeReason}
                                        onChange={(e) => handleAssetChange('statusChangeReason', e.target.value)}
                                        onBlur={() => handleAssetBlur('statusChangeReason')}
                                        placeholder="e.g. Not returned after the employee's last working day; reported to security on 3 Aug."
                                    />
                                </FormField>
                            )}
                        </div>
                        </section>

                        <section className="space-y-4 border-t pt-5">
                        <h4 className="text-sm font-semibold">Financials & Ownership</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                            <FormField id="costCenter" label="Cost Center" hint="Internal department for billing">
                                <Input value={assetFormData.costCenter} onChange={(e) => handleAssetChange('costCenter', e.target.value)} placeholder="IT-OPS-04" />
                            </FormField>
                            <FormField id="businessOwnerId" label="Business Owner" hint="The primary stakeholder for this asset">
                                <UserSelect
                                    id="businessOwnerId"
                                    users={users}
                                    value={assetFormData.businessOwnerId || null}
                                    onChange={(userId) => handleAssetChange('businessOwnerId', userId === null ? '' : String(userId))}
                                    placeholder="Select owner..."
                                    clearable
                                />
                            </FormField>
                            <FormField
                                id="usefulLifeYears"
                                label="Useful Life (Years)"
                                error={assetErrors.usefulLifeYears}
                                hint="Years before full depreciation — for rented assets, measured from Rental Start Date"
                            >
                                <Input
                                    type="number"
                                    value={assetFormData.usefulLifeYears}
                                    onChange={(e) => handleAssetChange('usefulLifeYears', e.target.value)}
                                    onBlur={() => handleAssetBlur('usefulLifeYears')}
                                    min="1"
                                />
                            </FormField>
                        </div>
                        </section>

                        <section className="space-y-4 border-t pt-5">
                        <h4 className="text-sm font-semibold">Acquisition Details</h4>

                        {/* Acquisition Type — segmented radio-card selector */}
                        <div>
                            <Label className="text-sm font-medium mb-2 block">Acquisition Type</Label>
                            <div role="radiogroup" aria-label="Acquisition Type" className="grid w-full max-w-sm grid-cols-2 gap-2">
                                {([['purchased', 'Purchased'], ['rented', 'Rented']] as const).map(([value, label]) => {
                                    const active = assetFormData.acquisitionType === value;
                                    return (
                                        <button
                                            key={value}
                                            type="button"
                                            role="radio"
                                            aria-checked={active}
                                            onClick={() => handleAssetChange('acquisitionType', value)}
                                            className={cn(
                                                "flex h-11 sm:h-10 items-center justify-center gap-2 rounded-md border text-sm font-medium transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                                                active
                                                    ? "border-primary bg-primary/5 text-primary"
                                                    : "border-input text-muted-foreground hover:bg-accent hover:text-foreground"
                                            )}
                                        >
                                            {active && <Check className="h-4 w-4" />}
                                            {label}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Purchased Asset Fields */}
                        {assetFormData.acquisitionType === 'purchased' && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                                <FormField id="purchaseDate" label="Purchase Date" hint="Date the asset was bought">
                                    <Input
                                        type="date"
                                        value={assetFormData.purchaseDate}
                                        onChange={(e) => handleAssetChange('purchaseDate', e.target.value)}
                                    />
                                </FormField>

                                <FormField
                                    id="purchaseCost"
                                    label="Purchase Cost"
                                    error={assetErrors.purchaseCost}
                                    hint="Total acquisition price"
                                >
                                    <Input
                                        type="number"
                                        step="0.01"
                                        value={assetFormData.purchaseCost}
                                        onChange={(e) => handleAssetChange('purchaseCost', e.target.value)}
                                        onBlur={() => handleAssetBlur('purchaseCost')}
                                        placeholder="0.00"
                                    />
                                    {renderDefaultCurrencyHint(assetFormData.purchaseCost)}
                                </FormField>

                                <FormField id="currency" label="Currency" hint="Currency this asset was purchased in">
                                    <select id="currency" className={SELECT_CLS} value={assetFormData.currency} onChange={(e) => handleAssetChange('currency', e.target.value)}>
                                        {availableCurrencies.map(c => <option key={c.code} value={c.code}>{c.code}</option>)}
                                    </select>
                                </FormField>

                                <FormField id="vendor" label="Vendor" hint="The supplier of this asset">
                                    <select id="vendor" className={SELECT_CLS} value={assetFormData.vendor} onChange={(e) => handleAssetChange('vendor', e.target.value)}>
                                        <option value="">Select vendor...</option>
                                        {masterVendors
                                            .filter(v => v.isActive !== false || v.name === assetFormData.vendor)
                                            .map(v => <option key={v.id} value={v.name}>{v.isActive === false ? `${v.name} (Inactive)` : v.name}</option>)}
                                    </select>
                                </FormField>
                                <FormField id="poNumber" label="PO Number" hint="Purchase Order reference">
                                    <Input value={assetFormData.poNumber} onChange={(e) => handleAssetChange('poNumber', e.target.value)} placeholder="PO-2024-001" />
                                </FormField>
                                <FormField id="invoiceNumber" label="Invoice Number" hint="Vendor invoice reference">
                                    <Input value={assetFormData.invoiceNumber} onChange={(e) => handleAssetChange('invoiceNumber', e.target.value)} placeholder="INV-XP-500" />
                                </FormField>
                                <FormField id="salvageValue" label={`Salvage Value (${formCurrencySymbol})`} hint="Estimated value at end of life">
                                    <Input type="number" step="0.01" value={assetFormData.salvageValue} onChange={(e) => handleAssetChange('salvageValue', e.target.value)} placeholder="0.00" />
                                    {renderDefaultCurrencyHint(assetFormData.salvageValue)}
                                </FormField>
                            </div>
                        )}

                        {/* Rented Asset Fields */}
                        {assetFormData.acquisitionType === 'rented' && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                                <FormField
                                    id="vendorMonthlyRent"
                                    label={`Monthly Rent (${formCurrencySymbol})`}
                                    hint="Reoccurring monthly rental fee"
                                >
                                    <Input
                                        type="number"
                                        step="0.01"
                                        value={assetFormData.vendorMonthlyRent}
                                        onChange={(e) => handleAssetChange('vendorMonthlyRent', e.target.value)}
                                        placeholder="0.00"
                                    />
                                    {renderDefaultCurrencyHint(assetFormData.vendorMonthlyRent)}
                                </FormField>

                                <FormField id="currency" label="Currency" hint="Currency the rent is billed in">
                                    <select id="currency" className={SELECT_CLS} value={assetFormData.currency} onChange={(e) => handleAssetChange('currency', e.target.value)}>
                                        {availableCurrencies.map(c => <option key={c.code} value={c.code}>{c.code}</option>)}
                                    </select>
                                </FormField>

                                <FormField id="receivedFromVendorDate" label="Rental Start Date" hint="When the rental tenure began">
                                    <Input
                                        type="date"
                                        value={assetFormData.receivedFromVendorDate}
                                        onChange={(e) => handleAssetChange('receivedFromVendorDate', e.target.value)}
                                    />
                                </FormField>

                                <FormField id="vendor" label="Vendor" hint="The supplier of this asset">
                                    <select id="vendor" className={SELECT_CLS} value={assetFormData.vendor} onChange={(e) => handleAssetChange('vendor', e.target.value)}>
                                        <option value="">Select vendor...</option>
                                        {masterVendors
                                            .filter(v => v.isActive !== false || v.name === assetFormData.vendor)
                                            .map(v => <option key={v.id} value={v.name}>{v.isActive === false ? `${v.name} (Inactive)` : v.name}</option>)}
                                    </select>
                                </FormField>
                            </div>
                        )}

                        </section>

                        <section className="space-y-4 border-t pt-5">
                        <h4 className="text-sm font-semibold">Warranty Details</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                            <FormField id="warrantyType" label="Warranty Type" hint="Type of support coverage">
                                <select id="warrantyType" className={SELECT_CLS} value={assetFormData.warrantyType} onChange={(e) => handleAssetChange('warrantyType', e.target.value)}>
                                    <option value="">Select type...</option>
                                    <option value="on-site">On-Site</option>
                                    <option value="depot">Depot (Carry-in)</option>
                                    <option value="care-pack">Extended CarePack</option>
                                </select>
                            </FormField>
                            <FormField id="warrantyStart" label="Warranty Start" hint="When coverage starts">
                                <Input type="date" value={assetFormData.warrantyStart} onChange={(e) => handleAssetChange('warrantyStart', e.target.value)} />
                            </FormField>
                            <FormField id="warrantyExpiry" label="Warranty Expiry" hint="When coverage ends">
                                <Input type="date" value={assetFormData.warrantyExpiry} onChange={(e) => handleAssetChange('warrantyExpiry', e.target.value)} />
                            </FormField>
                            <FormField id="maintenanceCycleDays" label="Maintenance Cycle (Days)" hint="Days between regular services">
                                <Input type="number" value={assetFormData.maintenanceCycleDays} onChange={(e) => handleAssetChange('maintenanceCycleDays', e.target.value)} placeholder="180" />
                            </FormField>
                        </div>
                        </section>

                        <section className="space-y-4 border-t pt-5">
                        <h4 className="text-sm font-semibold">Location Details</h4>
                        <FormField id="location" label="Location" hint="Where the asset is currently kept">
                            <Input
                                value={assetFormData.location}
                                onChange={(e) => handleAssetChange('location', e.target.value)}
                                placeholder="e.g. Office Floor 3, Desk 12"
                            />
                        </FormField>
                        </section>

                        <section className="space-y-4 border-t pt-5">
                        <h4 className="text-sm font-semibold">Additional Information</h4>
                        <FormField id="notes" label="Notes" hint="Any extra details or history">
                            <Textarea
                                rows={3}
                                value={assetFormData.notes}
                                onChange={(e) => handleAssetChange('notes', e.target.value)}
                                placeholder="Additional information about this asset"
                            />
                        </FormField>

                        <h4 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                            <Upload className="h-4 w-4" />
                            Asset Photos (Optional)
                        </h4>
                        <div className="space-y-4">
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/*"
                                multiple
                                onChange={handleImageSelect}
                                className="hidden"
                            />
                            <div
                                className="border-2 border-dashed rounded-lg p-8 flex flex-col items-center justify-center gap-2 hover:border-primary/50 transition-colors cursor-pointer bg-muted/30"
                                onClick={() => fileInputRef.current?.click()}
                            >
                                <Upload className="h-8 w-8 text-muted-foreground" />
                                <div className="text-center">
                                    <p className="text-sm font-medium">Click to upload photos</p>
                                    <p className="text-xs text-muted-foreground mt-1">PNG, JPG, WEBP up to 5MB each</p>
                                </div>
                            </div>
                            {imagePreviewUrls.length > 0 && (
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                                    {imagePreviewUrls.map((url, idx) => (
                                        <div key={idx} className="relative group rounded-lg overflow-hidden border bg-muted aspect-square">
                                            <img
                                                src={url}
                                                alt={`Preview ${idx + 1}`}
                                                className="w-full h-full object-cover"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => removeSelectedImage(idx)}
                                                className="absolute top-1 right-1 h-6 w-6 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-destructive/60"
                                            >
                                                <X className="h-3.5 w-3.5" />
                                            </button>
                                            <div className="absolute bottom-0 inset-x-0 bg-black/50 px-1.5 py-0.5">
                                                <p className="text-[10px] text-white truncate">{selectedImages[idx]?.name}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        </section>

                    </div>{/* /scrollable body */}

                    {/* Fixed footer (outside the scroll area at every breakpoint) */}
                    <DialogFooter className="mx-0 mt-0 shrink-0 border-t bg-background px-6 py-3 sm:border-t sm:bg-background sm:px-6 sm:py-3">
                        <Button type="button" variant="outline" onClick={() => setShowAssetModal(false)}>Cancel</Button>
                        <Button type="submit" disabled={submittingAsset}>
                            {submittingAsset ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
                            {submittingAsset ? 'Saving…' : (editMode ? 'Update Asset' : 'Create Asset')}
                        </Button>
                    </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog >

            {/* Deploy Dialog */}
            < Dialog open={showDeployModal} onOpenChange={setShowDeployModal} >
                <DialogContent className="sm:max-w-md">
                    <DialogHeader><DialogTitle>Deploy Asset</DialogTitle></DialogHeader>
                    <form onSubmit={handleDeploy} className="space-y-4 pt-4">
                        {allowedTargetTypes.length > 1 && (
                            <FormField id="targetType" label="Assignment Target" required={isDeployFieldRequired('targetType')} error={deployErrors.targetType}>
                                <select
                                    id="targetType"
                                    className={SELECT_CLS}
                                    value={deployFormData.targetType}
                                    onChange={(e) => handleDeployChange('targetType', e.target.value)}
                                >
                                    {allowedTargetTypes.map(t => <option key={t} value={t}>{t}</option>)}
                                </select>
                            </FormField>
                        )}

                        {deployFormData.targetType === 'PERSON' && (
                            <FormField
                                id="userId"
                                label="Assign To User"
                                required={isDeployFieldRequired('userId')}
                                error={deployErrors.userId}
                                hint="Select the staff member receiving this asset"
                            >
                                <UserSelect
                                    id="userId"
                                    users={users}
                                    value={deployFormData.userId || null}
                                    onChange={(userId) => {
                                        handleDeployChange('userId', userId === null ? '' : String(userId));
                                        // Preserves the original onBlur behaviour: picking a value
                                        // marks the field touched so validation can surface.
                                        handleDeployBlur('userId');
                                    }}
                                    placeholder="Select user..."
                                />
                            </FormField>
                        )}

                        {deployFormData.targetType === 'LOCATION' && (
                            <div className="space-y-4">
                                <FormField id="site" label="Site" hint="Phyical building or campus">
                                    <Input value={deployFormData.site} onChange={(e) => handleDeployChange('site', e.target.value)} placeholder="Main HQ" />
                                </FormField>
                                <div className="grid grid-cols-2 gap-4">
                                    <FormField id="building" label="Building">
                                        <Input value={deployFormData.building} onChange={(e) => handleDeployChange('building', e.target.value)} placeholder="Block A" />
                                    </FormField>
                                    <FormField id="floor" label="Floor">
                                        <Input value={deployFormData.floor} onChange={(e) => handleDeployChange('floor', e.target.value)} placeholder="3rd Floor" />
                                    </FormField>
                                </div>
                                {/* roomDesk has always been in the form state and
                                    in DeployAssetDto, but no control rendered it. */}
                                <FormField id="roomDesk" label="Room / Desk" hint="Exact desk or room within the floor">
                                    <Input value={deployFormData.roomDesk} onChange={(e) => handleDeployChange('roomDesk', e.target.value)} placeholder="Desk 12" />
                                </FormField>
                                <FormField
                                    id="location"
                                    label="Specific Location"
                                    required={isDeployFieldRequired('location')}
                                    error={deployErrors.location}
                                    hint="Room or desk number"
                                >
                                    <Input
                                        value={deployFormData.location}
                                        onChange={(e) => handleDeployChange('location', e.target.value)}
                                        onBlur={() => handleDeployBlur('location')}
                                        placeholder="Room 302 / Desk 12"
                                    />
                                </FormField>
                            </div>
                        )}

                        <FormField
                            id="deploymentDate"
                            label="Deployment Date"
                            required
                            error={deployErrors.deploymentDate}
                        >
                            {/* required is hardcoded: this is a UI-only rule (see DEPLOY_FORM_RULES) */}
                            <Input
                                type="date"
                                value={deployFormData.deploymentDate}
                                onChange={(e) => handleDeployChange('deploymentDate', e.target.value)}
                                onBlur={() => handleDeployBlur('deploymentDate')}
                            />
                        </FormField>

                        <FormField
                            id="deployReason"
                            label="Reason for Deployment"
                            required={isDeployFieldRequired('reason')}
                            error={deployErrors.reason}
                            hint="Explain why this asset is being deployed"
                        >
                            <Textarea
                                rows={3}
                                value={deployFormData.reason}
                                onChange={(e) => handleDeployChange('reason', e.target.value)}
                                onBlur={() => handleDeployBlur('reason')}
                                placeholder="e.g. New hire onboarding, replacement for faulty unit..."
                            />
                        </FormField>

                        <DialogFooter className="pt-4">
                            <Button type="button" variant="outline" onClick={() => setShowDeployModal(false)}>Cancel</Button>
                            <Button type="submit" disabled={submittingAction}>
                                {submittingAction ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />}
                                Deploy Asset
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog >

            {/* Undeploy Dialog */}
            <Dialog open={showUndeployModal} onOpenChange={setShowUndeployModal}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader><DialogTitle>Undeploy Asset</DialogTitle></DialogHeader>
                    <form onSubmit={handleUndeploy} className="space-y-4 pt-4">
                        <FormField
                            id="undeployReason"
                            label="Reason for Return"
                            required
                            error={undeployErrors.reason}
                            hint="Explain why this asset is being returned to inventory"
                        >
                            <Textarea
                                rows={3}
                                value={undeployFormData.reason}
                                onChange={(e) => handleUndeployChange('reason', e.target.value)}
                                onBlur={() => handleUndeployBlur('reason')}
                                placeholder="e.g. Employee offboarding, asset upgrade, damaged unit..."
                            />
                        </FormField>

                        <FormField
                            id="undeployCondition"
                            label="Condition on Return"
                            required
                            error={undeployErrors.condition}
                            hint="Physical state of the asset as received back"
                        >
                            <select
                                id="undeployCondition"
                                className={SELECT_CLS}
                                value={undeployFormData.condition}
                                onChange={(e) => handleUndeployChange('condition', e.target.value)}
                                onBlur={() => handleUndeployBlur('condition')}
                            >
                                <option value="">Select condition...</option>
                                <option value="new">New</option>
                                <option value="excellent">Excellent</option>
                                <option value="good">Good</option>
                                <option value="fair">Fair</option>
                                <option value="poor">Poor</option>
                                <option value="damaged">Damaged</option>
                                <option value="lost">Lost</option>
                            </select>
                        </FormField>

                        <DialogFooter className="pt-4">
                            <Button type="button" variant="outline" onClick={() => setShowUndeployModal(false)}>Cancel</Button>
                            <Button type="submit" disabled={submittingAction}>
                                {submittingAction ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />}
                                Undeploy Asset
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Maintenance Dialog */}
            <Dialog open={showMaintenanceModal} onOpenChange={setShowMaintenanceModal}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader><DialogTitle>Schedule Maintenance</DialogTitle></DialogHeader>
                    <form onSubmit={handleScheduleMaintenance} className="space-y-4 pt-4">
                        <div className="rounded-md bg-primary/10 border border-primary/20 p-3 text-sm text-primary flex items-start gap-2">
                            <Wrench className="h-4 w-4 mt-0.5 shrink-0" />
                            Scheduling maintenance will change the asset status and make it unavailable for deployment.
                        </div>

                        <FormField
                            id="lastMaintenanceDate"
                            label="Maintenance Start Date"
                            required
                            hint="When should the scheduled maintenance begin?"
                            error={maintenanceErrors.lastMaintenanceDate}
                        >
                            <Input
                                type="date"
                                value={maintenanceFormData.lastMaintenanceDate}
                                onChange={(e) => handleMaintenanceChange('lastMaintenanceDate', e.target.value)}
                                onBlur={() => handleMaintenanceBlur('lastMaintenanceDate')}
                            />
                        </FormField>

                        <FormField
                            id="nextMaintenanceDate"
                            label="Maintenance End Date"
                            required
                            hint="When should the scheduled maintenance end?"
                            error={maintenanceErrors.nextMaintenanceDate}
                        >
                            <Input
                                type="date"
                                value={maintenanceFormData.nextMaintenanceDate}
                                onChange={(e) => handleMaintenanceChange('nextMaintenanceDate', e.target.value)}
                                onBlur={() => handleMaintenanceBlur('nextMaintenanceDate')}
                                min={new Date().toISOString().split('T')[0]}
                            />
                        </FormField>

                        <FormField id="maintenanceNotes" label="Maintenance Notes" hint="Describe the maintenance work needed">
                            <Textarea
                                rows={3}
                                value={maintenanceFormData.maintenanceNotes}
                                onChange={(e) => handleMaintenanceChange('maintenanceNotes', e.target.value)}
                                placeholder="Describe the maintenance work..."
                            />
                        </FormField>

                        <DialogFooter className="pt-4">
                            <Button type="button" variant="outline" onClick={() => setShowMaintenanceModal(false)}>Cancel</Button>
                            <Button type="submit" variant="warning" disabled={submittingAction}>
                                {submittingAction ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <><Wrench className="h-4 w-4 mr-2" />Schedule Maintenance</>}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Maintenance Complete Dialog */}
            <Dialog open={showMaintenanceCompleteModal} onOpenChange={setShowMaintenanceCompleteModal}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader><DialogTitle>Complete Maintenance</DialogTitle></DialogHeader>
                    <form onSubmit={handleSubmitMaintenanceComplete} className="space-y-4 pt-4">
                        <div className="rounded-md bg-success/10 border border-success/20 p-3 text-sm text-success flex items-start gap-2">
                            <Check className="h-4 w-4 mt-0.5 shrink-0" />
                            Complete the maintenance record and return the asset to available status.
                        </div>

                        <FormField
                            id="maintenanceCompletedDate"
                            label="Completion Date"
                            required
                            error={maintenanceCompleteErrors.maintenanceCompletedDate}
                            hint="When was the work finished?"
                        >
                            <Input
                                type="date"
                                value={maintenanceCompleteFormData.maintenanceCompletedDate}
                                onChange={(e) => handleMaintenanceCompleteChange('maintenanceCompletedDate', e.target.value)}
                                onBlur={() => handleMaintenanceCompleteBlur('maintenanceCompletedDate')}
                            />
                        </FormField>

                        <FormField
                            id="workPerformed"
                            label="Work Performed"
                            required
                            error={maintenanceCompleteErrors.workPerformed}
                            hint="Detailed description of repairs/service"
                        >
                            <Textarea
                                rows={3}
                                value={maintenanceCompleteFormData.workPerformed}
                                onChange={(e) => handleMaintenanceCompleteChange('workPerformed', e.target.value)}
                                onBlur={() => handleMaintenanceCompleteBlur('workPerformed')}
                                placeholder="Describe what maintenance work was completed..."
                            />
                        </FormField>

                        <FormField id="completionNotes" label="Completion Notes" hint="Optional summary">
                            <Textarea
                                rows={2}
                                value={maintenanceCompleteFormData.completionNotes}
                                onChange={(e) => handleMaintenanceCompleteChange('completionNotes', e.target.value)}
                                placeholder="Additional notes..."
                            />
                        </FormField>

                        <FormField id="nextMaintenanceDate" label="Next Maintenance Date" hint="Optional: Set the next repeat cycle">
                            <Input
                                type="date"
                                value={maintenanceCompleteFormData.nextMaintenanceDate}
                                onChange={(e) => handleMaintenanceCompleteChange('nextMaintenanceDate', e.target.value)}
                                min={new Date().toISOString().split('T')[0]}
                            />
                        </FormField>

                        <DialogFooter className="pt-4">
                            <Button type="button" variant="outline" onClick={() => setShowMaintenanceCompleteModal(false)}>Cancel</Button>
                            <Button type="submit" variant="success" disabled={submittingAction}>
                                {submittingAction ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <><Check className="h-4 w-4 mr-2" />Complete Maintenance</>}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Disposal Dialog */}
            <Dialog open={showDisposalModal} onOpenChange={setShowDisposalModal}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader><DialogTitle>Dispose Asset</DialogTitle></DialogHeader>
                    <form onSubmit={handleDispose} className="space-y-4 pt-4">
                        <div className="rounded-md bg-warning/10 border border-warning/25 p-3 text-sm text-warning flex items-start gap-2">
                            <Recycle className="h-4 w-4 mt-0.5 shrink-0" />
                            This action will permanently dispose of the asset and change its status to "disposed".
                        </div>

                        <FormField
                            id="disposalDate"
                            label="Disposal Date"
                            required
                            error={disposalErrors.disposalDate}
                            hint="When was the asset removed?"
                        >
                            <Input
                                type="date"
                                value={disposalFormData.disposalDate}
                                onChange={(e) => handleDisposalChange('disposalDate', e.target.value)}
                                onBlur={() => handleDisposalBlur('disposalDate')}
                            />
                        </FormField>

                        <FormField
                            id="disposalMethod"
                            label="Disposal Method"
                            required
                            error={disposalErrors.disposalMethod}
                            hint="How was the disposal handled?"
                        >
                            <select
                                id="disposalMethod"
                                className={SELECT_CLS}
                                value={disposalFormData.disposalMethod}
                                onChange={(e) => handleDisposalChange('disposalMethod', e.target.value)}
                                onBlur={() => handleDisposalBlur('disposalMethod')}
                            >
                                <option value="">Select disposal method...</option>
                                {masterDisposalMethods.map(method => (
                                    <option key={method.id} value={method.value}>{method.label}</option>
                                ))}
                            </select>
                        </FormField>

                        <FormField
                            id="disposalReason"
                            label="Disposal Reason"
                            required
                            error={disposalErrors.disposalReason}
                            hint="Why is this asset being retired?"
                        >
                            <Textarea
                                rows={3}
                                value={disposalFormData.disposalReason}
                                onChange={(e) => handleDisposalChange('disposalReason', e.target.value)}
                                onBlur={() => handleDisposalBlur('disposalReason')}
                                placeholder="Explain why the asset is being disposed..."
                            />
                        </FormField>

                        <FormField id="disposalNotes" label="Additional Notes" hint="Optional summary">
                            <Textarea
                                rows={2}
                                value={disposalFormData.disposalNotes}
                                onChange={(e) => handleDisposalChange('disposalNotes', e.target.value)}
                                placeholder="Any additional notes..."
                            />
                        </FormField>

                        <DialogFooter className="pt-4">
                            <Button type="button" variant="outline" onClick={() => setShowDisposalModal(false)}>Cancel</Button>
                            <Button type="submit" variant="destructive" disabled={submittingAction}>
                                {submittingAction ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <><Trash2 className="h-4 w-4 mr-2" />Dispose Asset</>}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Import Preview Modal */}
            <AssetImportPreview
                open={showImportPreviewModal}
                onOpenChange={setShowImportPreviewModal}
                rows={importPreviewData}
                summary={importPreviewSummary}
                isImporting={isImporting}
                onConfirm={handleConfirmImport}
                onRowsChange={setImportPreviewData}
                takenAssetTagKeys={takenAssetTagKeys}
            />

            <ConfirmModal
                show={confirmState.show}
                title={confirmState.title}
                message={confirmState.message}
                type={confirmState.type}
                onConfirm={confirmState.onConfirm}
                onCancel={() => setConfirmState(prev => ({ ...prev, show: false }))}
            />

            <AssetAdvancedFilters
                open={showFilters}
                onOpenChange={setShowFilters}
                draft={qts.draftFilters}
                onDraftChange={qts.setDraftFilters}
                onApply={qts.applyDraft}
                onClear={qts.clearFilters}
                categoryOptions={categoryFilterOptions}
                brandOptions={brandFilterOptions}
                vendorOptions={vendorFilterOptions}
                statusOptions={statusFilterOptions}
                conditionOptions={conditionFilterOptions}
                departmentOptions={departmentFilterOptions}
                userOptions={userFilterOptions}
            />
        </div >
    );
};

export default AssetManagement;
